// @ts-strict-ignore
import { LanguageCode, Manifest } from 'mytours-read-api-typescript'
import { AppInfo, Language, AppMetaData } from 'mytours-read-common'

import strings, { languageStrings, availableLanguages } from '../../strings'
import { setDataToLocalStorage, getDataFromLocalStorage, removeDataFromLocalStorage } from '../platform'

// Received from backend, declared in views/layouts/mobile_web.html.erb
declare const appId: string
declare const projectId: string
declare const token: string
declare const demo_mode: boolean

export const DEFAULT_APP_INFO: AppInfo = {
	appId: appId,
	projectId: projectId,
	appApiKey: token,
}

export function getDefaultAppInfo(): AppInfo {
	return DEFAULT_APP_INFO
}

export function getAccountId() {
	return ENV.ACCOUNT_ID
}

export function shouldUsePublishedContent() {
	return ENV.PUBLISHING_MODEL_ENABLED && !demo_mode
}

/** The Basic-auth pair every content read carries, published subtree and direct API alike. */
export function getProjectCredentials(): { projectId: string; apiKey: string } {
	return { projectId: String(projectId), apiKey: token }
}

/**
 * Saves meta data to disk so can be loaded faster next time without hitting network.
 *
 * @param data data to save to disk.
 */
export function saveAppMetaDataToDisk(projectId: string, data: AppMetaData): Promise<void> {
	const key = projectId + '_metadata'
	return setDataToLocalStorage(key, data)
}

/**
 * Attempts to load app meta data from disk. We no longer store versions of meta data, just always the latest from server.
 */
export function loadAppMetaDataFromDisk(projectId: string): Promise<AppMetaData | undefined> {
	const key = projectId + '_metadata'
	return getDataFromLocalStorage(key)
}

export function saveAppManifestToDisk(projectId: string, data: Manifest): Promise<void> {
	const key = projectId + '_manifest'
	return setDataToLocalStorage(key, data)
}

export function loadAppManifestFromDisk(projectId: string): Promise<Manifest> {
	const key = projectId + '_manifest'
	return getDataFromLocalStorage(key)
}

export function saveDefaultLanguageCode(projectId: string, languageCode: string): Promise<void> {
	const key = projectId + '_language_code'
	return setDataToLocalStorage(key, languageCode, true)
}

export function loadDefaultLanguageCode(projectId: string): Promise<LanguageCode | undefined> {
	const key = projectId + '_language_code'
	return getDataFromLocalStorage(key, true)
}

export function saveCompletedOnboardingStep(projectId: string, onboardingStep: number): Promise<void> {
	const key = projectId + '_onboarding_step_' + onboardingStep
	return setDataToLocalStorage(key, true, true)
}

export function loadCompletedOnboardingStep(projectId: string, onboardingStep: number): Promise<boolean | undefined> {
	const key = projectId + '_onboarding_step_' + onboardingStep
	return getDataFromLocalStorage(key, true)
}

export function clearLocalStorage() {
	const { projectId } = DEFAULT_APP_INFO
	const storageKeys = ['_collection', '_screen', '_metadata', '_map_route', '_manifest', '_media_item']
	const keys = [...storageKeys.map(item => projectId + item), ...storageKeys.map(item => 'DEFAULT' + item)]

	removeDataFromLocalStorage(keys)
}

export const getLanguages = (): Language[] =>
	availableLanguages.map(code => ({
		name: strings[code.toLowerCase().split('-').join('_')],
		code,
	}))

const appLanguage = {
	currentLocale: '',
	primaryLocale: '',
	supportedLanguages: [],
	setCurrentLocale: (locale: string) => {
		appLanguage.currentLocale = locale
		strings.setLanguage(locale)
		saveDefaultLanguageCode(DEFAULT_APP_INFO.projectId, locale)
	},
	setPrimaryLocale: (locale: string) => {
		appLanguage.primaryLocale = locale
	},
	saveSupportedLanguages: (languageCodes: string[]) => {
		appLanguage.supportedLanguages = languageCodes
	},
}

export const getOriginalLanguageName = (languageCode: string): string =>
	languageStrings[languageCode]
		? languageStrings[languageCode][languageCode.toLowerCase().split('-').join('_')]
		: undefined

export default {
	appLanguage,
}
